import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { RotateCcw } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface TopicData {
  topic: string;
  accuracy: number;
  timeSpent: number;
  confidence: number;
}

interface TopicMasteryChartProps {
  data: TopicData[];
}

const getMasteryLevel = (accuracy: number) => {
  if (accuracy >= 80) return { label: "Mastered", color: "hsl(142, 71%, 45%)" };
  if (accuracy >= 60) return { label: "Proficient", color: "hsl(48, 96%, 53%)" };
  if (accuracy >= 40) return { label: "Learning", color: "hsl(25, 95%, 53%)" };
  return { label: "Beginner", color: "hsl(0, 84%, 60%)" };
};

export function TopicMasteryChart({ data }: TopicMasteryChartProps) {
  const navigate = useNavigate();

  const chartData = data.map(d => ({
    ...d,
    ...getMasteryLevel(d.accuracy),
  }));

  const weakTopics = data.filter(d => d.accuracy < 60);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Topic Mastery</CardTitle>
        <CardDescription>Accuracy by topic with mastery levels</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 80, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" domain={[0, 100]} tickFormatter={v => `${v}%`} />
              <YAxis type="category" dataKey="topic" width={75} tick={{ fontSize: 12 }} />
              <Tooltip
                formatter={(value: number) => [`${value}%`, "Accuracy"]}
                labelFormatter={(label) => {
                  const item = chartData.find(d => d.topic === label);
                  return `${label} — ${item?.label}`;
                }}
              />
              <Bar dataKey="accuracy" radius={[0, 4, 4, 0]}>
                {chartData.map((entry, idx) => (
                  <Cell key={idx} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-8">No topic data yet</p>
        )}

        {/* Legend */}
        <div className="flex flex-wrap gap-4 text-xs">
          {[
            { label: "Mastered (80%+)", color: "hsl(142, 71%, 45%)" },
            { label: "Proficient (60-79%)", color: "hsl(48, 96%, 53%)" },
            { label: "Learning (40-59%)", color: "hsl(25, 95%, 53%)" },
            { label: "Beginner (<40%)", color: "hsl(0, 84%, 60%)" },
          ].map(l => (
            <div key={l.label} className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: l.color }} />
              <span className="text-muted-foreground">{l.label}</span>
            </div>
          ))}
        </div>

        {/* Retake buttons for weak topics */}
        {weakTopics.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium">Topics needing improvement</p>
            <div className="flex flex-wrap gap-2">
              {weakTopics.map(t => (
                <Button
                  key={t.topic}
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/practice?topic=${encodeURIComponent(t.topic)}`)}
                >
                  <RotateCcw className="h-3 w-3 mr-1" />
                  Retake {t.topic} ({t.accuracy}%)
                </Button>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
